import React, { useState } from 'react'
import { signInWithEmailAndPassword } from 'firebase/auth'
import { auth } from 'services/firebase'
import { FirebaseError } from '@firebase/util'
import { NavLink, useNavigate } from 'react-router-dom'
import { Box, Button, Container, Stack, Text, useToast } from '@chakra-ui/react'
import { ProviderSection, TextInput } from 'components'
import styled from '@emotion/styled'

import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { emailRegex } from 'util/helpers'
import { Helmet } from 'react-helmet'

type FormData = {
  email: string
  password: string
}

const schema = yup
  .object({
    email: yup
      .string()
      .required('Please enter an email')
      .email('Invalid email')
      .matches(emailRegex, 'Invalid email format'),
    password: yup.string().required('Please enter a valid password'),
  })
  .required()

export const Login = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState<boolean>(false)
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
      password: '',
    },
  })
  const toast = useToast()

  const onLogin = async (data: FormData) => {
    const { email, password } = data
    setLoading(true)
    try {
      await signInWithEmailAndPassword(auth, email, password)
      toast({
        title: 'Logged in successfully',
        status: 'success',
        isClosable: true,
      })
      navigate('/')
    } catch (error) {
      if (error instanceof FirebaseError) {
        toast({
          title: 'Error while logging in',
          description: error.message,
          status: 'error',
          isClosable: true,
        })
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box w='100%' p={4}>
      <Helmet>
        <title>Login | Songvisor</title>
      </Helmet>
      <Container maxW='xl'>
        <form onSubmit={handleSubmit(onLogin)}>
          <Stack spacing={4}>
            <Controller
              name='email'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Email'
                  type='email'
                  placeholder='email@example.com'
                  isDisabled={loading}
                  isInvalid={Boolean(errors.email)}
                  helperText={errors.email?.message ? errors.email.message : ''}
                  isRequired
                  {...field}
                />
              )}
            />

            <Controller
              name='password'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Password'
                  type='password'
                  placeholder='*******'
                  isDisabled={loading}
                  isInvalid={Boolean(errors.password)}
                  helperText={errors.password?.message ? errors.password.message : ''}
                  isRequired
                  {...field}
                />
              )}
            />
          </Stack>
          <ButtonWrapper>
            <Button
              type='submit'
              colorScheme='gray'
              isLoading={loading}
              loadingText='Logging in'
              width='100%'
            >
              Login
            </Button>
          </ButtonWrapper>
        </form>

        <ProviderSection pageType='login' />

        <StyledText>
          No account yet? <NavLink to='/signup'>Sign up</NavLink>
        </StyledText>
      </Container>
    </Box>
  )
}

const ButtonWrapper = styled.div`
  width: 100%;
  margin-top: 2rem;
`
const StyledText = styled(Text)`
  margin-top: 1rem;

  a {
    font-weight: bold;
    &:hover {
      opacity: 60%;
    }
  }
`
